import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X, Sparkles, WifiOff, Wifi } from 'lucide-react';
import { Button } from './ui/button';
import { registerServiceWorker, skipWaiting } from '../serviceWorkerRegistration';

// Offline indicator component
export const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBackOnline, setShowBackOnline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setShowBackOnline(true);
      // Hide "back online" message after 3 seconds
      setTimeout(() => setShowBackOnline(false), 3000);
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      setShowBackOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <AnimatePresence>
      {isOffline && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-0 left-0 right-0 z-[200] bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2 safe-area-top"
        >
          <WifiOff className="w-4 h-4" />
          <span>Sei offline - Alcune funzioni potrebbero non essere disponibili</span>
        </motion.div>
      )}
      {showBackOnline && !isOffline && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-0 left-0 right-0 z-[200] bg-emerald-500 text-white py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2 safe-area-top"
        >
          <Wifi className="w-4 h-4" />
          <span>Connessione ripristinata</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Register service worker and listen for updates
    registerServiceWorker(() => {
      setShowUpdate(true);
    });
  }, []);

  const handleUpdate = () => {
    setIsUpdating(true);
    skipWaiting();
    // The page will refresh automatically via controllerchange event
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  return (
    <AnimatePresence>
      {showUpdate && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[100] w-[90%] max-w-md"
        >
          <div className="bg-gradient-to-r from-sky-600 to-blue-700 text-white rounded-2xl shadow-2xl shadow-blue-500/30 p-4 flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Nuova versione disponibile!</p>
              <p className="text-xs text-white/80 mt-0.5">
                Aggiorna per ottenere le ultime funzionalità
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={handleUpdate}
                disabled={isUpdating}
                size="sm"
                className="bg-white text-blue-700 hover:bg-white/90 font-medium text-xs px-3 h-8"
              >
                {isUpdating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  'Aggiorna'
                )}
              </Button>
              
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Chiudi"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Componente per mostrare il toast di conferma dopo l'aggiornamento
export const UpdatedToast = () => {
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    // Check if we just updated
    const justUpdated = sessionStorage.getItem('servicare_just_updated');
    if (justUpdated) {
      setShowToast(true);
      sessionStorage.removeItem('servicare_just_updated');
      
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
    
    // Set flag for next reload (when SW updates and reloads the page)
    const setUpdateFlag = () => {
      sessionStorage.setItem('servicare_just_updated', 'true');
    };
    
    // Listen for beforeunload during SW update
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', setUpdateFlag);
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', setUpdateFlag);
      }
    };
  }, []);

  return (
    <AnimatePresence>
      {showToast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[100]"
        >
          <div className="bg-emerald-600 text-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">App aggiornata all'ultima versione!</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
